import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import InputSpinner from 'react-bootstrap-input-spinner';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Api from '../../services/Api';
import * as urls from '../shared/urls';
import Step from '../shared/Step';
import { IKeywords, IReport, IWorkflowItemStep } from '../../common/sharedTypes';
import KeywordsController from '../Forms/Keywords/controller/KeywordsController';
import { openReport, setKeywords } from '../WorkflowQueues/Management/redux-objects/actions';
import { openReport as keywordsOpenReport } from '../WorkflowQueues/Keywords/redux-objects/actions';
import { SplitCaseResponse } from './SplitCaseResponse';

type SplitCaseButtonReduxProps = {
  keywords: IKeywords[];
  setKeywords: (keywords: IKeywords[]) => Promise<void>;
  openReport: (workflowItemStepId: number) => Promise<never>;
  keywordsOpenReport: (workflowItemStep: IWorkflowItemStep) => Promise<IWorkflowItemStep>;
  openWorkflowItemStep: IWorkflowItemStep;
}

type SplitCaseButtonProps = {
  reportId: number;
  openWorkflowItemStepId: number;
  variant?: string;
  disabled?: boolean;
  buttonText?: string;
  afterSplit?: () => void;
  buttonClassName?: string;
};

function SplitCaseButton({
  reportId,
  openWorkflowItemStepId,
  variant,
  disabled,
  buttonText,
  keywords,
  afterSplit,
  buttonClassName,
  setKeywords,
  openReport,
  keywordsOpenReport,
  openWorkflowItemStep,
}: SplitCaseButtonProps & SplitCaseButtonReduxProps) {
  const [showModal, setShowModal] = useState(false);
  const [numSplitCases, setNumSplitCases] = useState(0);
  const [finishClickLoading, setfinishClickLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [splitCaseResponse, setSplitCaseResponse] = useState<SplitCaseResponse>(null);
  const [isLoadingSplitData, setIsLoadingSplitData] = useState(false);

  useEffect(() => {
    if (openWorkflowItemStep) {
      keywordsOpenReport(openWorkflowItemStep);
    }
  }, [keywordsOpenReport, openWorkflowItemStep]);

  const onSplitClicked = async () => {
    if (!reportId || !openWorkflowItemStepId) {
      toast.error('Unable to split case: missing report context.');
      return;
    }

    setShowModal(true);
    setIsLoadingSplitData(true);

    try {
      const response = await Api.get<IReport>(`${urls.GET_REPORT_WITH_KEYWORDS}?reportId=${reportId}`);
      await openReport(openWorkflowItemStepId);

      if (!response.ok || !response.data || !response.data.keywords) {
        setShowModal(false);
        toast.error('Failed to retrieve keywords for report');
        console.error({ response });
        return;
      }

      setKeywords([response.data.keywords]);
    } catch (error) {
      setShowModal(false);
      toast.error('Failed to load split data for report');
      console.error({ error });
    } finally {
      setIsLoadingSplitData(false);
    }
  };

  const onModalClose = () => {
    setShowModal(false);
    setNumSplitCases(0);
    setKeywords([]);
    setCurrentStep(1);
    setSplitCaseResponse(null);
    setIsLoadingSplitData(false);
  };

  const onNumSplitCasesChange = (number: number) => {
    if (number < 1) {
      return;
    }

    if (!keywords || keywords.length === 0 || !keywords[0]) {
      toast.error('Keywords are still loading. Please wait and try again.');
      return;
    }

    setNumSplitCases(number);

    const splitKeywords = [...Array(number)].map((_n, i) => ({
      ...keywords[0],
      id: -(i + 1)
    }));

    const keywordsForSplitting = [
      keywords[0],
      ...splitKeywords
    ];

    setKeywords(keywordsForSplitting);
  };

  const onFinishClicked = async () => {

    setfinishClickLoading(true);
    const response = await Api.post<SplitCaseResponse>(urls.SAVE_MANAGEMENT_SPLIT, { keywords });

    if (!response.ok) {
      toast.error('Error splitting report');
      console.error({ response });
      onModalClose();
      setfinishClickLoading(false);
      return;
    }

    setSplitCaseResponse(response.data);
    setCurrentStep(currentStep + 1)
    setfinishClickLoading(false);

  };

  const onFinalConfirmationClicked = () => {
    onModalClose();

    if (afterSplit) afterSplit();
  }

  return (
    <>
      <Button
        variant={variant || 'secondary'}
        disabled={disabled || false}
        onClick={onSplitClicked}
        className={buttonClassName}
      >
        {buttonText || `Split`}
      </Button>

      <Modal
        show={showModal}
        aria-labelledby='contained-modal-title-vcenter'
        centered
        size={splitCaseResponse ? 'lg' : 'xl'}
        onHide={onModalClose}
      >
        <Modal.Header closeButton>
          {currentStep == 1 && <b>Manage Report</b>}
          {currentStep == 2 && <b>Keywords for current case</b>}
          {currentStep > 2 && currentStep <= numSplitCases + 2 && (
            <b>Keywords for case {currentStep - 2}</b>
          )}
          {currentStep == numSplitCases + 3 && <b>Split Cases End</b>}
        </Modal.Header>
        <Modal.Body>
          <Step stepNumber={1} currentStep={currentStep}>
            <Col xs={4}></Col>
            <Col xs={4}>
              <div style={{ textAlign: 'center' }}>
                <p>
                  <b>How many times do you want to split this case?</b>
                </p>
                <InputSpinner
                  type={'real'}
                  precision={2}
                  max={20}
                  min={1}
                  step={1}
                  value={numSplitCases}
                  onChange={onNumSplitCasesChange}
                  variant={'dark'}
                  size="sm"
                  disabled={isLoadingSplitData}
                />
              </div>
            </Col>
            <Col xs={4}></Col>
          </Step>
          {keywords?.map((keyword, index) => (
            <Step key={keyword.id} stepNumber={index + 2} currentStep={currentStep}>
              <KeywordsController
                keywordData={keyword}
                showNotificationTemplateSelector={false}
                splitCaseMode={true}
              />
            </Step>
          ))}
          <Step stepNumber={numSplitCases + 3} currentStep={currentStep}>
            {splitCaseResponse && (
              <div>
                <p>Split case <b>{keywords[0].originalCrashKey}</b> into</p>
                <FinishedSplitsCardStyles>
                  {splitCaseResponse.splitCases.length > 0 && splitCaseResponse.splitCases.map((splitCase) => (
                    <Card>
                      <Card.Body>
                        <ul className='p-0 m-0'>
                          <li>
                            <h6>Original Crash Key</h6>
                            {splitCase.originalCrashKey}
                          </li>
                          <li>
                            <h6>Document ID</h6>
                            {splitCase.documentId}
                          </li>
                        </ul>
                      </Card.Body>
                    </Card>
                  ))}
                </FinishedSplitsCardStyles>
              </div>
            )}
          </Step>
        </Modal.Body>
        <Modal.Footer>
          {currentStep > 1 && currentStep <= numSplitCases + 2 && (
            <Button
              variant='warning'
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Previous
            </Button>
          )}
          {currentStep <
            numSplitCases + 2 && (
              <Button
                variant='success'
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={numSplitCases < 1 || isLoadingSplitData}
              >
                Next
              </Button>
            )}
          {currentStep ==
            numSplitCases + 2 && (
              <Button variant='success' onClick={onFinishClicked}  disabled={finishClickLoading}>
                {finishClickLoading ? "Finishing..." : "Finish"}
              </Button>
            )}
          {splitCaseResponse && (
            <Button 
              variant='success' 
              onClick={onFinalConfirmationClicked} 
            >
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

const mapStateToProps = (reduxState: any) => {
  return {
    keywords: reduxState.Management.keywords,
    openWorkflowItemStep: reduxState.Management.openWorkflowItemStep,
  };
};

const mapDispatchToProps = {
  setKeywords,
  openReport,
  keywordsOpenReport,
};

export default connect(mapStateToProps, mapDispatchToProps)(SplitCaseButton);

const FinishedSplitsCardStyles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .card {
    width: 15rem;
  }
`;
